import React, { useEffect, useRef, useState } from "react";
//@ts-ignore
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import { Typography, Button, Grid, Fade, useTheme } from "@material-ui/core";
import { useSelector } from "react-redux";
import { selectActiveGroupMembers, selectCleverlynk, selectIsMobile } from "../../redux/slices/cleverlynkSlice";
import useMapService from "../../hooks/MapServiceHooks/useMapService";
import { selectUserLocation } from "../../redux/slices/mapServiceSlice";

import { useTranslation } from "react-i18next";
import "../../i18n/config";

export const CoverageMapColorScheme = {
  0: "#ED6A5E",
  1: "#4CE0B3",
  2: "#FF8E72",
  3: "#156064",
  4: "#F8E16C",
  5: "#e6194b",
  6: "#3cb44b",
  7: "#ffe119",
  8: "#4363d8",
};

const FALLBACK_LNG = -74.0259;
const FALLBACK_LAT = 4.746;
const FALLBACK_ZOOM = 10;

interface ICoverageMap {
  onConfirm: (clynkId: string) => void;
}

const CoverageMap = ({ onConfirm }: ICoverageMap) => {
  const [selectedArea, setSelectedArea] = useState<{ name: string; id: string } | null>(null);

  const mapContainer = useRef(null);
  const map = useRef<any>(null);
  const selectedAreaIDRef = useRef<string | undefined>(undefined);

  const activeGroupMembers = useSelector(selectActiveGroupMembers);
  const cleverlynk = useSelector(selectCleverlynk);
  const userLocation = useSelector(selectUserLocation);
  const isMobile = useSelector(selectIsMobile);

  const { t } = useTranslation("deliveryinput");

  const theme = useTheme();

  const { getCoordinatesArrayFromClynk } = useMapService();

  mapboxgl.accessToken = process.env.REACT_APP_MAPBOX;

  useEffect(() => {
    if (map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/gregorioospina/ckwehc3p40dst14nl5sjwnyxl",
      center: [FALLBACK_LNG, FALLBACK_LAT],
      zoom: FALLBACK_ZOOM,
    });
  });

  useEffect(() => {
    if (!map.current) return;
    map.current.setCenter([
      userLocation?.lng ?? cleverlynk?.geolocationInfo?.location?.lng ?? FALLBACK_LNG,
      userLocation?.lat ?? cleverlynk?.geolocationInfo?.location?.lat ?? FALLBACK_LAT,
    ]);
  }, [userLocation, cleverlynk]);

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize
    map.current.on("load", async () => {
      for (const index in activeGroupMembers) {
        if (activeGroupMembers[index].open) {
          await map.current.addSource(`source-${index}`, {
            type: "geojson",
            data: {
              promoteId: "este",
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: getCoordinatesArrayFromClynk(activeGroupMembers[index]?.geolocationInfo!),
              },
            },
          });
          await map.current.addLayer({
            id: `layer-${index}`,
            type: "fill",
            source: `source-${index}`,
            paint: {
              "fill-color": CoverageMapColorScheme[Number(index) % 8],
              "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.8, 0.3],
            },
          });
        }
      }
      for (const index in activeGroupMembers) {
        if (activeGroupMembers[index].open) {
          map.current.on("mousemove", `layer-${index}`, function (e) {
            if (selectedAreaIDRef.current === `layer-${index}`) {
              map.current.setPaintProperty(`layer-${index}`, "fill-opacity", 0.8);
            } else {
              map.current.setPaintProperty(`layer-${index}`, "fill-opacity", 0.5);
            }
          });
          map.current.on("mouseleave", `layer-${index}`, function () {
            if (selectedAreaIDRef.current !== `layer-${index}`) {
              map.current.setPaintProperty(`layer-${index}`, "fill-opacity", 0.3);
            }
          });
          map.current.on("click", `layer-${index}`, function () {
            if (selectedAreaIDRef.current && selectedAreaIDRef.current !== `layer-${index}`) {
              map.current.setPaintProperty(selectedAreaIDRef.current, "fill-opacity", 0.3);
            }
            map.current.setPaintProperty(`layer-${index}`, "fill-opacity", 0.6);
            selectedAreaIDRef.current = `layer-${index}`;
            setSelectedArea(
              activeGroupMembers[index]
                ? { name: activeGroupMembers[index].name!, id: activeGroupMembers[index].id! }
                : null
            );
          });
        }
      }
    });
  });

  return (
    <div style={{ position: "relative", marginTop: 5 }}>
      <div
        ref={mapContainer}
        style={{
          height: isMobile ? window.innerHeight * 0.6 : 500,
          maxHeight: window.innerHeight * 0.6,
          width: "inherit",
          borderRadius: 9,
        }}></div>
      <Fade in={true}>
        <Grid
          container
          alignItems="center"
          style={{
            backgroundColor: !selectedArea ? "#ffffff" : theme.palette.primary.main,
            padding: 8,
            position: "absolute",
            bottom: 0,
            borderRadius: "0px 0px 9px 9px",
            zIndex: 999,
            width: "100%",
          }}
          wrap="nowrap"
          justifyContent="space-between">
          {selectedArea ? (
            <React.Fragment>
              <Grid
                item
                container
                direction="column"
                style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
                <Grid item style={{ height: 15, marginBottom: 8 }}>
                  <Typography variant="overline" style={{ textTransform: "none" }}>
                    {t("coverageMap.youveChosen")} {"  "}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" style={{ fontSize: "1.1rem" }}>
                    {selectedArea.name}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  style={{
                    backgroundColor: "white",
                    color: "black",
                    border: `1pt solid ${theme.palette.primary.main}`,
                  }}
                  variant="contained"
                  onClick={() => onConfirm(selectedArea.id)}>
                  {t("coverageMap.confirm")}
                </Button>
              </Grid>
            </React.Fragment>
          ) : (
            <Grid item style={{ height: 15, marginBottom: 8, width: "100%" }} container justifyContent="center">
              <Typography variant="overline" align="center" style={{ fontWeight: "bold", fontSize: "0.6rem" }}>
                {t("coverageMap.chooseArea")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Fade>
    </div>
  );
};
export default CoverageMap;
